import { useState } from "react";
import { Utensils, ScrollText, Scale, Sun, CalendarHeart, Crown, Play, X } from "lucide-react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { Reveal } from "./Reveal";
import { trackEvent } from "@/lib/analytics";

const teachings = [
  {
    n: "01",
    icon: Utensils,
    title: "The Dietary Law",
    ref: "Leviticus 11",
    body: "What we consume is not indifferent to God. He set apart the clean from the unclean, and He never rescinded it.",
    videoId: "a8IFlUCBGNY",
  },
  {
    n: "02",
    icon: ScrollText,
    title: "Keeping the Commandments Holy",
    ref: "Exodus 20",
    body: "The commandments are not a burden but a covenant of love. To love Him is to keep them — every one.",
  },
  {
    n: "03",
    icon: Scale,
    title: "Obeying the Laws",
    ref: "Matthew 5:17",
    body: "Christ came not to abolish the law but to fulfill it. Not one jot or tittle shall pass away.",
    videoId: "D5uaLKCMRh8",
  },
  {
    n: "04",
    icon: Sun,
    title: "The Sabbath Day",
    ref: "Genesis 2:3",
    body: "The seventh day was blessed and sanctified. We keep the Sabbath holy, exactly as He instructed.",
  },
  {
    n: "05",
    icon: CalendarHeart,
    title: "His Holy Feast Days",
    ref: "Leviticus 23",
    body: "Passover, Unleavened Bread, Firstfruits, Pentecost, Trumpets, Atonement, Tabernacles — His appointed times.",
    videoId: "qWbhrKTE1vA",
  },
  {
    n: "06",
    icon: Crown,
    title: "The Kingdom of Heaven",
    ref: "Matthew 6:33",
    body: "Seek first the Kingdom and His righteousness. Salvation is on the line — and so we restore what was removed.",
  },
];

export const Teachings = () => {
  const [videoId, setVideoId] = useState(null);
  const open = Boolean(videoId);

  const play = (t) => {
    setVideoId(t.videoId);
    trackEvent("play_video", { label: t.title });
  };

  return (
    <section id="teachings" data-testid="teachings-section" className="bg-[#0A0A0A] py-24 md:py-32">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <Reveal>
          <p className="text-xs font-semibold tracking-[0.3em] uppercase text-[#D4AF37] mb-4">
            What Was Taken Out
          </p>
          <h2 className="font-heading font-light tracking-tight text-4xl md:text-6xl max-w-3xl leading-tight">
            The teachings we are <span className="italic text-[#D4AF37]">restoring</span>.
          </h2>
        </Reveal>

        <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 border-t border-l border-[#2B2825]">
          {teachings.map((t, i) => {
            const hasVideo = Boolean(t.videoId);
            return (
              <Reveal key={t.n} delay={(i % 3) * 0.08}>
                <article
                  data-testid={`teaching-card-${t.n}`}
                  onClick={hasVideo ? () => play(t) : undefined}
                  role={hasVideo ? "button" : undefined}
                  tabIndex={hasVideo ? 0 : undefined}
                  onKeyDown={hasVideo ? (e) => (e.key === "Enter" || e.key === " ") && play(t) : undefined}
                  className={`group relative h-full border-r border-b border-[#2B2825] p-8 md:p-10 transition-colors duration-500 hover:bg-[#141311] ${hasVideo ? "cursor-pointer" : ""}`}
                >
                  <div className="absolute inset-x-0 top-0 h-px bg-[#D4AF37] scale-x-0 group-hover:scale-x-100 origin-left transition-transform duration-500" />
                  <div className="flex items-start justify-between mb-8">
                    <t.icon strokeWidth={1} className="h-8 w-8 text-[#D4AF37]" />
                    <span className="font-mono text-sm text-[#4A453F] group-hover:text-[#A39E98] transition-colors">
                      {t.n}
                    </span>
                  </div>
                  <h3 className="font-heading text-2xl md:text-3xl font-light mb-2 text-[#F4F0EA]">
                    {t.title}
                  </h3>
                  <p className="font-mono text-xs tracking-wider uppercase text-[#D4AF37]/80 mb-5">
                    {t.ref}
                  </p>
                  <p className="text-base leading-relaxed font-light text-[#A39E98]">
                    {t.body}
                  </p>
                  {hasVideo && (
                    <span
                      data-testid={`teaching-watch-${t.n}`}
                      className="mt-6 inline-flex items-center gap-2 text-sm font-medium text-[#D4AF37] group-hover:text-[#E5C865] transition-colors"
                    >
                      <span className="flex h-8 w-8 items-center justify-center rounded-full border border-[#D4AF37]">
                        <Play strokeWidth={1.5} className="h-3.5 w-3.5 translate-x-px" fill="currentColor" />
                      </span>
                      Watch the teaching
                    </span>
                  )}
                </article>
              </Reveal>
            );
          })}
        </div>
      </div>

      <DialogPrimitive.Root open={open} onOpenChange={(o) => !o && setVideoId(null)}>
        <DialogPrimitive.Portal>
          <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-black/90 backdrop-blur-sm data-[state=open]:animate-in data-[state=open]:fade-in-0" />
          <DialogPrimitive.Content
            data-testid="teaching-video-modal"
            className="fixed left-1/2 top-1/2 z-50 w-[92vw] max-w-5xl -translate-x-1/2 -translate-y-1/2 focus:outline-none data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95"
          >
            <DialogPrimitive.Title className="sr-only">Teaching Video</DialogPrimitive.Title>
            <DialogPrimitive.Description className="sr-only">Podcast video for this teaching.</DialogPrimitive.Description>
            <DialogPrimitive.Close
              data-testid="teaching-video-close"
              className="absolute -top-12 right-0 flex items-center gap-2 text-[#A39E98] hover:text-[#F4F0EA] transition-colors"
            >
              <span className="text-sm tracking-wide">Close</span>
              <X strokeWidth={1} className="h-5 w-5" />
            </DialogPrimitive.Close>
            <div className="aspect-video w-full border border-[#4A453F] bg-black">
              {open && (
                <iframe
                  className="h-full w-full"
                  src={`https://www.youtube-nocookie.com/embed/${videoId}?autoplay=1&rel=0`}
                  title="Teaching"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  referrerPolicy="strict-origin-when-cross-origin"
                  allowFullScreen
                />
              )}
            </div>
          </DialogPrimitive.Content>
        </DialogPrimitive.Portal>
      </DialogPrimitive.Root>
    </section>
  );
};
